import { useState } from 'react';

import { api } from '../api/index.ts';
import { setLang, useT } from '../i18n/index.ts';
import { karachiDate } from '../lib/dates.ts';
import { rs } from '../lib/money.ts';
import { navigate } from '../lib/router.ts';
import { ConfirmDialog } from '../ui/ConfirmDialog.tsx';
import { Icon } from '../ui/icons.tsx';
import { Num, OfflinePill } from '../ui/parts.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { useOnline, useSpender } from './store.tsx';
import { WalletGrid } from './WalletGrid.tsx';
import { ThemeToggle } from '../ui/ThemeToggle.tsx';

/** Renders "<b>…</b>" from a translation as bold, keeping word order per language. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/<b>(.*?)<\/b>/).map((part, i) =>
        i % 2 ? (
          <Num key={i} className="font-bold">
            {part}
          </Num>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/** Full-width column shared by header, content and footer so their edges line up. */
const COLUMN = 'w-full px-4 sm:px-6 lg:px-10';

/** `ownerApp`: this person also uses the owner app (a role with "Records own purchases"). */
export function Home({ onLogout, ownerApp = false }: { onLogout: () => void; ownerApp?: boolean }) {
  const { t, lang } = useT();
  const online = useOnline();
  const s = useSpender();
  const [ackFlash, setAckFlash] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const today = karachiDate();
  // Money out of his own wallets only: a purchase another portfolio paid for isn't his
  // spending (owner's request, 14 Oct 2026).
  const todays = s.entries.filter((e) => e.txnDate === today && e.status !== 'rejected' && !e.paidByEntityId);
  const queued = s.entries.filter((e) => e.syncState !== 'synced').length;
  const cashIn = s.cashIns[0];

  async function acknowledge(id: string, entityId: string) {
    navigator.vibrate?.(8);
    await api.acknowledge(id, entityId);
    setAckFlash(true);
    window.setTimeout(() => setAckFlash(false), 2400);
    await s.refresh();
  }

  function switchLang() {
    const next = lang === 'ur' ? 'en' : 'ur';
    setLang(next);
    void api.setLanguage(next);
  }

  return (
    <div className="flex min-h-app flex-col">
      <header className="safe-top shrink-0 border-b-[1.5px] border-line bg-paper">
        <div className={`${COLUMN} flex h-16 items-center justify-between pe-2 low:h-14 short:h-14 sm:pe-6 lg:pe-10`}>
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="text-[13px] font-semibold text-muted">{t('home.hello')}</span>
            <span className="text-fluid-title truncate leading-tight font-bold">{s.me.fullName}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {!online && <OfflinePill />}
            <button
              type="button"
              onClick={switchLang}
              className={`h-12 min-w-14 rounded-[10px] border-[1.5px] border-line-strong bg-paper px-3 text-[17px] font-bold hover:bg-ground ${lang === 'ur' ? 'font-sans' : 'font-urdu'}`}
            >
              {t('lang.switchTo')}
            </button>
            <ThemeToggle
              labels={{ dark: t('theme.dark'), light: t('theme.light') }}
              className="h-12 min-w-12 rounded-[10px] border-[1.5px] border-line-strong bg-paper text-ink"
            />
            <NotificationBell />
            {ownerApp && (
              <button
                type="button"
                aria-label={t('home.ownerApp')}
                title={t('home.ownerApp')}
                onClick={() => navigate('/admin')}
                className="flex h-12 min-w-12 items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-line-strong bg-paper px-3 text-[15px] font-semibold hover:bg-ground"
              >
                <Icon name="dashboard" size={20} />
                <span className="hidden sm:inline">{t('home.ownerApp')}</span>
              </button>
            )}
            {/* Icon-only on phones; icon + word from small tablets up. Confirms first, since a
                spender who logs out by accident must sign in again with a code. */}
            <button
              type="button"
              aria-label={t('home.logout')}
              title={t('home.logout')}
              onClick={() => setConfirmLogout(true)}
              className="flex h-12 min-w-12 items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-line-strong bg-paper px-3 text-[15px] font-semibold hover:bg-ground"
            >
              <Icon name="logout" size={20} flip />
              <span className="hidden sm:inline">{t('home.logout')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`${COLUMN} flex flex-1 flex-col gap-3 pt-3.5 pb-4 low:gap-2 low:pt-2.5 short:gap-2 short:pt-2 sm:pt-5`}>
        {cashIn && !ackFlash && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] border-line-strong bg-paper p-3 ps-3.5 sm:flex-nowrap"
            style={{ borderInlineStart: `6px solid ${s.entityById(cashIn.entityId)?.colour ?? '#13211F'}` }}
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-ground">
              <Icon name="wallet" />
            </span>
            <span className="min-w-0 flex-1 basis-40 text-[15px] leading-snug rtl:text-[17px]">
              <Rich
                text={t('home.recharge', {
                  name: cashIn.givenBy,
                  amount: rs(cashIn.amount),
                  entity: s.entityById(cashIn.entityId)?.name ?? '',
                })}
              />
            </span>
            <button
              type="button"
              onClick={() => void acknowledge(cashIn.id, cashIn.entityId)}
              className="ms-auto h-12 shrink-0 rounded-[10px] bg-brand px-3.5 text-base font-bold text-white hover:bg-brand-dark"
            >
              {t('home.received')}
            </button>
          </div>
        )}
        {ackFlash && (
          <div
            role="status"
            className="flex h-13 items-center gap-2.5 rounded-xl bg-success-tint px-3.5 text-[15px] font-bold text-success-text"
          >
            <Icon name="check" stroke={2.5} size={20} />
            {t('home.receivedDone')}
          </div>
        )}
        {queued > 0 && (
          <div className="flex min-h-12 items-center gap-2.5 rounded-xl border-[1.5px] border-dashed border-ink bg-paper px-3.5 py-2 text-[15px] font-bold">
            <Icon name="sync" size={20} />
            {t('home.waiting', { count: queued })}
          </div>
        )}

        <div className="mt-1 flex min-h-12 items-center gap-2">
          <span className="flex-1 text-sm font-bold text-muted">{t('home.cashWithYou')}</span>
          {s.isCustomOrder && (
            <button
              type="button"
              onClick={s.resetOrder}
              className="h-12 px-2 text-sm font-semibold text-muted underline underline-offset-4 hover:text-ink"
            >
              {t('home.arrangeReset')}
            </button>
          )}
        </div>
        {/* Tap a house: add an expense or give cash from it; press and hold 2 s, then drag, to reorder. */}
        <WalletGrid />

        <button
          type="button"
          onClick={() => navigate('/history')}
          className="flex min-h-16 items-center gap-3 rounded-[14px] border-[1.5px] border-line-strong bg-paper px-3.5 py-2 text-start hover:bg-ground low:min-h-14 short:min-h-12 short:py-1"
        >
          <span className="flex flex-1 flex-col leading-snug">
            <span className="text-sm font-semibold text-muted">{t('home.spentToday')}</span>
            <span className="text-[17px] font-bold rtl:text-[19px]">
              <Num>{rs(todays.reduce((a, e) => a + e.amount, 0))}</Num> · {t('home.entries', { count: todays.length })}
            </span>
          </span>
          <Icon name="chevronEnd" flip />
        </button>
      </main>

      <footer className="safe-bottom sticky bottom-0 border-t-[1.5px] border-line-strong bg-paper pt-3">
        {/* Phones: two equal halves with a gap between. Wider screens: History at the start,
            Add expense at the end, space between. */}
        {/* Text, height and icon scale with the screen width (text-fluid-button, h-fluid-button,
            icon-fluid); a label that still doesn't fit wraps to a second line instead of being cut. */}
        <div className={`${COLUMN} grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-[clamp(8px,2.5vw,12px)] sm:flex sm:justify-between`}>
          <button
            type="button"
            onClick={() => navigate('/history')}
            className="icon-fluid text-fluid-button h-fluid-button flex min-w-0 items-center justify-center gap-[clamp(4px,1.5vw,8px)] rounded-[14px] border-[1.5px] border-line-strong bg-paper px-[clamp(6px,2.5vw,12px)] leading-tight font-bold hover:bg-ground short:h-12 sm:h-16 sm:w-56"
          >
            <Icon name="history" />
            <span className="min-w-0 text-center break-words">{t('home.history')}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate(`/add?entity=${s.lastEntityId}`)}
            className="icon-fluid text-fluid-button h-fluid-button flex min-w-0 items-center justify-center gap-[clamp(4px,1.5vw,8px)] rounded-[14px] bg-brand px-[clamp(6px,2.5vw,12px)] leading-tight font-bold text-white hover:bg-brand-dark short:h-12 sm:h-16 sm:w-80"
          >
            <Icon name="plus" size={24} stroke={2.75} />
            <span className="min-w-0 text-center break-words">{t('home.add')}</span>
          </button>
        </div>
      </footer>

      <ConfirmDialog
        open={confirmLogout}
        icon="logout"
        title={t('home.logoutTitle')}
        body={t('home.logoutBody')}
        confirmLabel={t('home.logout')}
        cancelLabel={t('app.cancel')}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          onLogout();
        }}
      />
    </div>
  );
}
